import { computed, inject, Injectable, Injector } from '@angular/core';

import { WcgFilterIndicatorsOptions } from '@shared/interfaces/wcg-table-utils.interface';
import {
  BaseGridViewState,
  BASE_GRID_VIEW_STATE,
} from '@data-access/extendables';
import {
  SubmissionListSignalState,
  SubmissionListItem,
  BatchAssignmentsPayload,
  AssignmentPayload,
} from '@data-access/submissions/types';
import { updateGridFilter } from '@data-access/shared';
import { WcgMenuOption } from '@wcg/frontend-framework';
import { mapSmeQcUserToMenuOption } from '../utils';

@Injectable({
  providedIn: 'root',
})
export class SubmissionListFacadeService extends BaseGridViewState {
  private readonly _submissionGridStore =
    inject<SubmissionListSignalState>(BASE_GRID_VIEW_STATE);
  private readonly _injector = inject(Injector);

  readonly $submissions = computed(() =>
    this._submissionGridStore.$submissions(),
  );
  readonly $currentUrl = computed(() =>
    this._submissionGridStore.$currentUrl(),
  );

  readonly $submissionListScope = computed(() =>
    this._submissionGridStore.$submissionListScope(),
  );

  readonly $isUpdatingSMEQC = computed(() =>
    this._submissionGridStore.$isUpdatingSMEQC(),
  );

  readonly $isAllSelected = computed(() =>
    this._submissionGridStore.$isAllSelected(),
  );
  readonly $isIndeterminate = computed(() =>
    this._submissionGridStore.$isIndeterminate(),
  );
  readonly $selectedRows = computed(() =>
    this._submissionGridStore.$selectedRows(),
  );
  readonly $isInternal = computed(() =>
    this._submissionGridStore.$isInternal(),
  );

  readonly $smeList = computed(() => this._submissionGridStore.$smeList());
  readonly $qcList = computed(() => this._submissionGridStore.$qcList());

  readonly $smeUserListOptions = computed<WcgMenuOption[]>(() => {
    const smeList = this.$smeList() ?? [];

    return smeList.map((user) =>
      mapSmeQcUserToMenuOption(user, this._injector),
    );
  });

  readonly $qcUserListOptions = computed<WcgMenuOption[]>(() => {
    const qcList = this.$qcList() ?? [];

    return qcList.map((user) => mapSmeQcUserToMenuOption(user, this._injector));
  });

  /**
   * NOTE: We are going forward with using inject() instead of constructor injection
   * But Super class needs to have the parameters passed in constructor
   * which we cannot do in the parent class for this specific signal store.
   * Hence, we are using inject() here and passing the values to super()
   */
  constructor() {
    super();
  }

  clearSearchString(): void {
    this._submissionGridStore.updateSearchString('');
  }

  updateSubmissionGridFilter(filter: WcgFilterIndicatorsOptions) {
    const _constructFilter = updateGridFilter(
      filter,
      this._submissionGridStore.$advancedFilters(),
    );
    this.updateGrid(
      'filter',
      {
        filters: _constructFilter.filters,
        page: this._submissionGridStore.$pageView().page,
        pageSize: this._submissionGridStore.$pageView().pageSize,
      },
      true,
      false,
    );
  }

  toggleRow(row: SubmissionListItem, checked: boolean) {
    this._submissionGridStore.selectRow(row, checked);
  }

  toggleSelectAll(checked: boolean) {
    this._submissionGridStore.selectAll(checked);
  }

  resetSelectedRows(): void {
    this._submissionGridStore.resetSelectedRows();
  }

  batchUpdateWorkspaceSMEQC(assignments: BatchAssignmentsPayload): void {
    this._submissionGridStore.batchUpdateWorkspaceSMEQC(assignments);
  }

  batchRemoveSMEQC(assignments: BatchAssignmentsPayload): void {
    this._submissionGridStore.batchRemoveSMEQC(assignments);
  }

  updateWorkspaceSMEQC(assignment: AssignmentPayload): void {
    this._submissionGridStore.updateWorkspaceSMEQC(assignment);
  }

  removeWorkspaceSMEQC(assignment: AssignmentPayload): void {
    this._submissionGridStore.removeWorkspaceSMEQC(assignment);
  }

  reloadSubmissionList(updateColumns?: boolean): void {
    this._submissionGridStore.reloadSubmissionList(updateColumns);
  }
}
